<script setup lang="ts">
const device = useScanDevice()
const online = useOnline()
const { drain, refreshPending } = useSupportRequest()

// Always present, so the setup screen looks like every screen after it. What it can
// say depends on whether the phone has been paired yet.
const isPaired = computed(() => device.value.provisioned)

const isDoor = computed(() => device.value.purpose === 'access_control')

/**
 * The quiet line: whose phone this is.
 *
 * Only a partner's phone needs one, because its own label is just "Device 1" and the
 * partner would otherwise be named nowhere. A door phone is ours, so there is no partner
 * to name and nothing to put here.
 */
const headerContext = computed(() => isDoor.value
  ? ''
  : [device.value.partnerName, device.value.deviceLabel].filter(Boolean).join(' · '))

/**
 * The bold line: which phone this is, in the fewest words that stay true all day.
 *
 * A door phone names its room and stops there. It deliberately does not name the
 * workshop: the door screen already shows the sitting properly, with the time and day
 * that actually identify it, and repeating a long title up here only truncated it to
 * something unreadable while saying the room twice.
 */
const headerPlace = computed(() => isDoor.value
  ? device.value.deviceLabel
  : (device.value.areaName || 'No area selected'))

const showSupport = ref(false)

onMounted(() => {
  refreshPending()
  drain()
})

// Only support requests are retried on their own. A message asking for help is no use
// sitting on the phone, and there is nobody to press a button when the thing being
// reported is that the phone cannot reach us.
//
// Scans are deliberately not sent here. Anything captured offline stays waiting until
// someone uploads it by hand at the end of the day, which is the agreed routine.
watch(online, (isOnline) => {
  if (isOnline) {
    drain()
  }
})
</script>

<template>
  <div>
    <header class="scan-header">
      <WadLogo />

      <template v-if="isPaired">
        <!-- Two lines in one flexible column rather than three separate items competing
             for a phone's width, which truncated both of them mid-word. -->
        <span class="scan-header__identity">
          <span v-if="headerContext" class="scan-header__context">{{ headerContext }}</span>
          <span
            class="scan-header__place"
            :class="{ 'scan-header__place--solo': !headerContext }"
          >{{ headerPlace }}</span>
        </span>

        <button type="button" class="scan-header__help" @click="showSupport = true">
          Need help?
        </button>
      </template>
    </header>

    <main>
      <slot />
    </main>

    <SupportModal v-if="showSupport" @close="showSupport = false" />
  </div>
</template>

<style lang="scss" scoped>
.scan-header {
  position: sticky;
  top: 0;
  z-index: 2;
  box-sizing: border-box;
  height: $wad4-header-height-total;
  display: flex;
  align-items: center;
  gap: $wad4-spacing-lg;
  padding: 0 $wad4-container-padding;
  padding-top: env(safe-area-inset-top, 0px);
  background-color: $wad4-c-background-bg-primary;
  color: $wad4-c-foreground-fg-primary;
  border-bottom: 1px solid $wad4-c-border-border-subtle;

  // The mark is a fixed 56px. Pinned so the identity column absorbs the slack rather
  // than the logo being squeezed by a long room name.
  :deep(.wad-logo) {
    flex: none;
  }

  // Takes the space the logo and the help button do not, so a long room name has
  // somewhere to go instead of being cut after four characters.
  &__identity {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
  }

  &__context {
    @include text-size-mixin('xs');
    color: $wad4-c-text-text-secondary;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__place {
    @include text-size-mixin('md');
    font-weight: $wad4-font-weight-medium;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    // A door phone has no partner line above this, so the room is the only thing the
    // header says and it can take the space that leaves. It is also the one piece of
    // context that stays true all day, which is what earns it the size.
    &--solo {
      @include text-size-mixin('lg');
    }
  }

  // Small enough to avoid accidental taps, still reachable in one. Never shrinks or
  // wraps: it wrapped to two lines once the row ran out of space, which made the header
  // look broken on the screen where someone is most likely to need it.
  &__help {
    flex: none;
    padding: $wad4-spacing-2 $wad4-spacing-3;
    font-family: $wad4-default-font-sans;
    @include text-size-mixin('xs');
    color: $wad4-c-text-text-secondary;
    white-space: nowrap;
    background: none;
    border: 1px solid $wad4-c-border-border-primary;
    border-radius: $wad4-border-radius-full;
    cursor: pointer;
  }
}
</style>
